import React, { useState, useEffect, useRef, useMemo } from 'react';
import html2canvas from 'html2canvas';
import './Generator.css';
import coursesData from '../courses.json';
import studentCoursesData from '../studentCourses.json';
import TimetableGrid from '../components/Timetable/TimetableGrid';
import EditTiming from '../components/FullTimetable/EditTiming';

function parseTimingStr(timingStr) {
    if (!timingStr) return [];
    const timings = timingStr.split(',');
    return timings.map((t) => {
        const dayCode = t[0];
        const start = t.slice(1, 5);
        const end = t.slice(5, 9);
        const days = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday' };
        return { day: days[dayCode], start, end };
    });
}

function toMinutes(t) {
    return parseInt(t.substring(0, 2), 10) * 60 + parseInt(t.substring(2, 4), 10);
}

// Semester window for ICS export. Update these each semester.
const SEMESTER_START = new Date(2026, 0, 5); // Jan 5, 2026 (Monday)
const SEMESTER_END = new Date(2026, 4, 1);   // May 1, 2026

const DAY_TO_WEEKDAY = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
};

function pad2(n) {
    return n.toString().padStart(2, '0');
}

function firstDateOnOrAfter(startDate, weekday) {
    const d = new Date(startDate);
    const diff = (weekday - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + diff);
    return d;
}

function formatDateYMD(date) {
    return `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;
}

function formatUtcStamp(date) {
    return (
        `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
        `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
    );
}

function escapeIcsText(text) {
    return String(text)
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n');
}

export default function Generator() {
    const [allCourses, setAllCourses] = useState([]);
    const [selectedCourses, setSelectedCourses] = useState(() => {
        try {
            const saved = localStorage.getItem('selectedCourses');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [timetableData, setTimetableData] = useState(() => {
        try {
            const saved = localStorage.getItem('timetableData');
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });
    const [expandedCourse, setExpandedCourse] = useState(null);

    const [showAutoFetch, setShowAutoFetch] = useState(false);
    const [kerberosId, setKerberosId] = useState('');
    const [autoFetchLoading, setAutoFetchLoading] = useState(false);
    const [fetchLog, setFetchLog] = useState('');
    const [fetchStatus, setFetchStatus] = useState(null);

    const timetableRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        setAllCourses(coursesData);
    }, []);

    useEffect(() => {
        localStorage.setItem('selectedCourses', JSON.stringify(selectedCourses));
        localStorage.setItem('timetableData', JSON.stringify(timetableData));
    }, [selectedCourses, timetableData]);

    const addCourses = (courseCodes, clearExisting = false) => {
        const newCourses = [];
        const newTimetableUpdates = {};

        courseCodes.forEach((courseCode) => {
            if (!clearExisting && selectedCourses.find((c) => c.courseCode === courseCode)) return;
            if (newCourses.find((c) => c.courseCode === courseCode)) return;

            const course = allCourses.find((c) => c.courseCode === courseCode);
            if (!course) return;

            newTimetableUpdates[courseCode] = {
                lecture: parseTimingStr(course.slot.lectureTiming),
                tutorial: null,
                lab: null,
            };

            newCourses.push({
                courseCode: course.courseCode,
                courseName: course.courseName,
                instructor: course.instructor,
                lecture: !!course.slot.lectureTiming,
                tutorial: course.creditStructure.split('-')[1] !== '0.0',
                lab: course.creditStructure.split('-')[2] !== '0.0',
                lectureTiming: parseTimingStr(course.slot.lectureTiming),
                tutorialTiming: parseTimingStr(course.slot.tutorialTiming),
                labTiming: parseTimingStr(course.slot.labTiming),
                creditStructure: course.creditStructure,
                totalCredits: course.totalCredits,
                lectureHall: course.lectureHall,
            });
        });

        if (newCourses.length === 0) return;

        setTimetableData((prev) => (clearExisting ? newTimetableUpdates : { ...prev, ...newTimetableUpdates }));
        setSelectedCourses((prev) => (clearExisting ? newCourses : [...prev, ...newCourses]));
        setSearchQuery('');
    };

    const addCourse = (courseCode) => addCourses([courseCode]);

    const removeCourse = (courseCode) => {
        setSelectedCourses(selectedCourses.filter((c) => c.courseCode !== courseCode));
        const newData = { ...timetableData };
        delete newData[courseCode];
        setTimetableData(newData);
        if (expandedCourse === courseCode) setExpandedCourse(null);
    };

    const toggleExpand = (courseCode) => {
        setExpandedCourse(expandedCourse === courseCode ? null : courseCode);
    };

    const handleAutoFetch = () => {
        if (!kerberosId.trim()) {
            setFetchLog('Enter a Kerberos ID.');
            setFetchStatus('err');
            return;
        }
        setAutoFetchLoading(true);
        setFetchStatus(null);
        setFetchLog('Looking up courses…');
        try {
            const lowerId = kerberosId.toLowerCase().trim();
            const foundCourses = studentCoursesData[lowerId];
            if (foundCourses && foundCourses.length > 0) {
                setFetchLog(`Found ${foundCourses.length} course${foundCourses.length === 1 ? '' : 's'}: ${foundCourses.join(', ')}`);
                setFetchStatus('ok');
                addCourses(foundCourses, true);
                setTimeout(() => {
                    setShowAutoFetch(false);
                    setFetchLog('');
                    setFetchStatus(null);
                    setKerberosId('');
                    setAutoFetchLoading(false);
                }, 1400);
            } else {
                setFetchLog('No courses found for this Kerberos ID.');
                setFetchStatus('err');
                setAutoFetchLoading(false);
            }
        } catch (error) {
            setFetchLog(`Error: ${error.message}`);
            setFetchStatus('err');
            setAutoFetchLoading(false);
        }
    };

    const generateICS = () => {
        const CRLF = '\r\n';
        const lines = [];
        lines.push('BEGIN:VCALENDAR');
        lines.push('VERSION:2.0');
        lines.push('PRODID:-//IITD Timetable//EN');
        lines.push('CALSCALE:GREGORIAN');
        lines.push('METHOD:PUBLISH');

        // Fixed-offset VTIMEZONE for Asia/Kolkata (no DST).
        lines.push('BEGIN:VTIMEZONE');
        lines.push('TZID:Asia/Kolkata');
        lines.push('BEGIN:STANDARD');
        lines.push('DTSTART:19700101T000000');
        lines.push('TZOFFSETFROM:+0530');
        lines.push('TZOFFSETTO:+0530');
        lines.push('TZNAME:IST');
        lines.push('END:STANDARD');
        lines.push('END:VTIMEZONE');

        const dtstamp = formatUtcStamp(new Date());
        // UNTIL is end of SEMESTER_END day in IST, expressed in UTC (23:59:59 IST = 18:29:59 UTC).
        const untilDate = new Date(Date.UTC(
            SEMESTER_END.getFullYear(),
            SEMESTER_END.getMonth(),
            SEMESTER_END.getDate(),
            18, 29, 59
        ));
        const untilStr = formatUtcStamp(untilDate);

        selectedCourses.forEach((course) => {
            const data = timetableData[course.courseCode];
            if (!data) return;
            const events = [];
            if (data.lecture) events.push(...data.lecture.map((e) => ({ ...e, type: 'Lecture' })));
            if (data.tutorial) events.push(...data.tutorial.map((e) => ({ ...e, type: 'Tutorial' })));
            if (data.lab) events.push(...data.lab.map((e) => ({ ...e, type: 'Lab' })));
            events.forEach((event) => {
                const weekday = DAY_TO_WEEKDAY[event.day];
                if (!weekday) return;
                const firstDate = firstDateOnOrAfter(SEMESTER_START, weekday);
                const datePart = formatDateYMD(firstDate);
                const startTime = `${event.start.slice(0, 2)}${event.start.slice(2, 4)}00`;
                const endTime = `${event.end.slice(0, 2)}${event.end.slice(2, 4)}00`;
                const loc = event.location || course.lectureHall || '';
                const byday = event.day.slice(0, 2).toUpperCase();
                const summary = `${course.courseCode} ${event.type}${loc ? ` (${loc})` : ''}`;
                const uid = `${course.courseCode}-${event.type}-${event.day}-${event.start}@iitd-timetable`;

                lines.push('BEGIN:VEVENT');
                lines.push(`UID:${uid}`);
                lines.push(`DTSTAMP:${dtstamp}`);
                lines.push(`SUMMARY:${escapeIcsText(summary)}`);
                lines.push(`DTSTART;TZID=Asia/Kolkata:${datePart}T${startTime}`);
                lines.push(`DTEND;TZID=Asia/Kolkata:${datePart}T${endTime}`);
                lines.push(`RRULE:FREQ=WEEKLY;BYDAY=${byday};UNTIL=${untilStr}`);
                if (loc) lines.push(`LOCATION:${escapeIcsText(loc)}`);
                lines.push('END:VEVENT');
            });
        });

        lines.push('END:VCALENDAR');
        const ics = lines.join(CRLF) + CRLF;

        const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'iitd-timetable.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    const handleDownloadImage = async () => {
        if (!timetableRef.current) return;
        const scrollContainer = timetableRef.current.querySelector('.tt');
        if (!scrollContainer) return;
        const w = scrollContainer.scrollWidth;
        const h = scrollContainer.scrollHeight;
        try {
            const canvas = await html2canvas(timetableRef.current, {
                scale: 2,
                backgroundColor: '#FBF9F4',
                logging: false,
                useCORS: true,
                width: w,
                height: h,
                windowWidth: w,
                windowHeight: h,
                onclone: (clonedDoc) => {
                    const node = clonedDoc.querySelector('.tt');
                    if (node) {
                        node.style.overflow = 'visible';
                        node.style.width = w + 'px';
                        node.style.height = h + 'px';
                        node.style.maxWidth = 'none';
                        node.style.maxHeight = 'none';
                    }
                },
            });
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = 'iitd-timetable.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading image:', err);
        }
    };

    const filteredCourses = useMemo(() => {
        if (!searchQuery) return [];
        return allCourses
            .filter((c) => c.courseCode.includes(searchQuery.toUpperCase()))
            .slice(0, 10);
    }, [searchQuery, allCourses]);

    // Stats: total credits and conflict count
    const stats = useMemo(() => {
        let credits = 0;
        const sessions = [];
        selectedCourses.forEach((course) => {
            credits += parseFloat(course.totalCredits || 0) || 0;
            const cd = timetableData[course.courseCode];
            if (!cd) return;
            ['lecture', 'tutorial', 'lab'].forEach((k) => {
                if (cd[k]) {
                    cd[k].forEach((s) => {
                        if (s && s.day && s.start && s.end) {
                            sessions.push({ ...s, courseCode: course.courseCode });
                        }
                    });
                }
            });
        });
        let conflicts = 0;
        for (let i = 0; i < sessions.length; i++) {
            for (let j = i + 1; j < sessions.length; j++) {
                const a = sessions[i];
                const b = sessions[j];
                if (a.day !== b.day || a.courseCode === b.courseCode) continue;
                if (toMinutes(a.start) < toMinutes(b.end) && toMinutes(b.start) < toMinutes(a.end)) {
                    conflicts++;
                }
            }
        }
        return { credits, conflicts };
    }, [selectedCourses, timetableData]);

    // Close search on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    return (
        <section className="gen">
            <header className="gen__head">
                <div className="gen__head-text">
                    <div className="gen__eyebrow">IIT Delhi · Semester planner</div>
                    <h1 className="gen__title">
                        Build your <em>week</em>, the way it actually fits.
                    </h1>
                    <p className="gen__sub">
                        Search courses, configure your tutorials and labs, catch conflicts, and export
                        the result to your calendar or as an image.
                    </p>
                </div>

                <div className="gen__stats" aria-label="Schedule summary">
                    <div className="gen__stat">
                        <span className="gen__stat-label">Courses</span>
                        <span className="gen__stat-value tnum">{selectedCourses.length}</span>
                    </div>
                    <div className="gen__stat">
                        <span className="gen__stat-label">Credits</span>
                        <span className="gen__stat-value tnum">{stats.credits.toFixed(1)}</span>
                    </div>
                    <div className="gen__stat">
                        <span className="gen__stat-label">Conflicts</span>
                        <span className={'gen__stat-value tnum' + (stats.conflicts > 0 ? ' gen__stat-value--warn' : '')}>
                            {stats.conflicts}
                        </span>
                    </div>
                </div>
            </header>

            <div className="gen__toolbar" ref={searchRef}>
                <div className="gen__search">
                    <span className="gen__search-icon" aria-hidden="true">
                        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </span>
                    <input
                        type="text"
                        className="gen__search-input"
                        placeholder="Search by course code (COL106, ELL201, MTL106…)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />

                    {searchQuery && filteredCourses.length > 0 && (
                        <div className="gen__results" role="listbox">
                            {filteredCourses.map((c) => (
                                <div
                                    key={c.courseCode}
                                    className="gen__result"
                                    role="option"
                                    aria-selected={false}
                                    tabIndex={0}
                                    onClick={() => addCourse(c.courseCode)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCourse(c.courseCode);
                                    }}
                                >
                                    <div>
                                        <span className="gen__result-code">{c.courseCode}</span>
                                        {c.lectureHall && <span className="gen__result-loc">{c.lectureHall}</span>}
                                    </div>
                                    <span className="gen__result-credits">{c.creditStructure}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    className={'btn gen__autofetch-toggle' + (showAutoFetch ? ' btn--primary' : '')}
                    onClick={() => {
                        setShowAutoFetch((v) => !v);
                        setFetchLog('');
                        setFetchStatus(null);
                    }}
                    aria-expanded={showAutoFetch}
                >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Auto-fetch
                </button>
            </div>

            {showAutoFetch && (
                <div className="gen__autofetch">
                    <p className="gen__autofetch-help">
                        Enter your Kerberos ID to pull your registered courses.
                    </p>
                    <div className="gen__autofetch-input-wrap">
                        <label htmlFor="kerberos" className="field-label">Kerberos ID</label>
                        <input
                            id="kerberos"
                            type="text"
                            className="field field--mono"
                            placeholder="kerberos id"
                            value={kerberosId}
                            onChange={(e) => setKerberosId(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAutoFetch();
                            }}
                            autoFocus
                            disabled={autoFetchLoading}
                        />
                    </div>
                    <button
                        className="btn btn--primary"
                        onClick={handleAutoFetch}
                        disabled={autoFetchLoading || !kerberosId.trim()}
                    >
                        {autoFetchLoading ? 'Fetching…' : 'Fetch courses'}
                    </button>
                    <button
                        className="btn btn--ghost"
                        onClick={() => {
                            setShowAutoFetch(false);
                            setFetchLog('');
                            setFetchStatus(null);
                        }}
                        disabled={autoFetchLoading}
                    >
                        Cancel
                    </button>
                    {fetchLog && (
                        <div
                            className={
                                'gen__autofetch-status' +
                                (fetchStatus === 'ok' ? ' gen__autofetch-status--ok' : '') +
                                (fetchStatus === 'err' ? ' gen__autofetch-status--err' : '')
                            }
                        >
                            {fetchLog}
                        </div>
                    )}
                </div>
            )}

            <div className="gen__main">
                <div className="gen__col-board" ref={timetableRef}>
                    <TimetableGrid timetable={selectedCourses} timetableData={timetableData} />

                    <div className="gen__legend" aria-label="Legend">
                        <span className="gen__legend-item">
                            <span className="gen__legend-swatch gen__legend-swatch--lec" /> Lecture
                        </span>
                        <span className="gen__legend-item">
                            <span className="gen__legend-swatch gen__legend-swatch--tut" /> Tutorial
                        </span>
                        <span className="gen__legend-item">
                            <span className="gen__legend-swatch gen__legend-swatch--lab" /> Lab
                        </span>
                        <span className="gen__legend-item">
                            <span className="gen__legend-swatch gen__legend-swatch--conf" /> Conflict
                        </span>
                    </div>

                    {stats.conflicts > 0 && (
                        <div className="gen__conflict" role="alert">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>
                                {stats.conflicts} overlap{stats.conflicts === 1 ? '' : 's'} detected — adjust tutorial/lab timings to resolve.
                            </span>
                        </div>
                    )}
                </div>

                <aside className="gen__col-side">
                    <div className="gen__side-head">
                        <h2 className="gen__side-title">
                            Selected
                            <span className="gen__side-count tnum">{selectedCourses.length.toString().padStart(2, '0')}</span>
                        </h2>
                    </div>

                    {selectedCourses.length > 0 && (
                        <div className="gen__actions">
                            <button className="btn btn--primary" onClick={generateICS}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                Export .ics
                            </button>
                            <button className="btn" onClick={handleDownloadImage}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Save image
                            </button>
                        </div>
                    )}

                    {selectedCourses.length === 0 ? (
                        <div className="gen__empty">
                            <div className="gen__empty-title">No courses yet</div>
                            <div className="gen__empty-sub">
                                Search above or use auto-fetch to start building your week.
                            </div>
                        </div>
                    ) : (
                        <div className="gen__course-list">
                            {selectedCourses.map((course) => {
                                const isExpanded = expandedCourse === course.courseCode;
                                return (
                                    <div key={course.courseCode} className="gen__course">
                                        <div className="gen__course-row">
                                            <div className="gen__course-info">
                                                <span className="gen__course-code">{course.courseCode}</span>
                                                <span className="gen__course-meta">
                                                    <span>{course.creditStructure}</span>
                                                    {course.lectureHall && (
                                                        <>
                                                            <span className="gen__course-meta-dot" />
                                                            <span>{course.lectureHall}</span>
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="gen__course-actions">
                                                <button
                                                    className="btn btn--sm btn--ghost"
                                                    onClick={() => toggleExpand(course.courseCode)}
                                                    aria-expanded={isExpanded}
                                                >
                                                    {isExpanded ? 'Done' : 'Edit'}
                                                </button>
                                                <button
                                                    className="btn btn--sm btn--icon btn--danger-ghost"
                                                    onClick={() => removeCourse(course.courseCode)}
                                                    title="Remove"
                                                    aria-label={`Remove ${course.courseCode}`}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="5" y1="12" x2="19" y2="12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="gen__course-edit">
                                                <EditTiming
                                                    div_id={`edit-${course.courseCode}`}
                                                    data={course}
                                                    timetableData={timetableData}
                                                    setTimetableData={setTimetableData}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
}
